import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import type { Tag } from "@gtm/contracts/core/tags";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { apiClient, unwrap } from "@/lib/api-client";

export function TagEditor({
  open = true,
  tag,
  onClose,
  onSaved,
}: {
  tag: Tag | null;
  onClose: () => void;
  onSaved?: (tag: Tag) => void;
  open?: boolean;
}) {
  const [name, setName] = useState(tag?.name || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const client = useQueryClient();
  useDialogReset(open, () => {
    setName(tag?.name || "");
    setBusy(false);
    setError(null);
  });
  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (tag)
        await unwrap(
          apiClient().PATCH("/tags/{id}", { params: { path: { id: tag.id } }, body: { name } }),
        );
      else {
        const saved = await unwrap(apiClient().POST("/tags", { body: { name } }));
        onSaved?.(saved);
      }
      void client.invalidateQueries();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      title={tag ? "Rename tag" : "Create tag"}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label="Save"
            isLoading={busy}
            isDisabled={busy || !name.trim()}
            onClick={() => void save()}
          />
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Input
          aria-label="Tag name"
          placeholder="Tag name"
          value={name}
          onChange={(event) => setName(event)}
          autoFocus
          disabled={busy}
          label="Tag name"
        />
      </form>
      {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
    </SurfaceDialog>
  );
}
